import { useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export function useSessionTracking() {
  const { user } = useAuth();
  const hasTracked = useRef(false);
  const lastTrackTime = useRef<number>(0);

  const trackSession = useCallback(async () => {
    if (!user) return;

    // Throttle: only track once per 5 minutes
    const now = Date.now();
    if (now - lastTrackTime.current < 5 * 60 * 1000) {
      return;
    }
    lastTrackTime.current = now;

    try {
      const { data, error } = await supabase.functions.invoke("track-session", {
        body: {
          userAgent: navigator.userAgent,
        },
      });

      if (error) {
        console.error("Session tracking error:", error);
        return;
      }

      // Show warning if suspicious activity detected
      if (data?.suspicious && data?.warning) {
        toast.warning(data.warning, {
          duration: 10000,
          description: "If this wasn't you, please change your password immediately.",
        });
      }
    } catch (error) {
      console.error("Failed to track session:", error);
    }
  }, [user]);

  // Track on mount and when user changes
  useEffect(() => {
    if (user && !hasTracked.current) {
      hasTracked.current = true;
      trackSession();
    }
  }, [user, trackSession]);

  // Track periodically while user is active
  useEffect(() => {
    if (!user) return;

    // Track on visibility change (when tab becomes visible)
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        trackSession();
      }
    };

    // Track on user activity (throttled)
    const handleActivity = () => {
      trackSession();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    
    // Track every 15 minutes while active
    const interval = setInterval(trackSession, 15 * 60 * 1000);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearInterval(interval);
    };
  }, [user, trackSession]);

  return { trackSession };
}
